import { Card, CardBody } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useComparableField } from '../../../context/useComparableField'
import type { ComparableValue } from '../../../types/diffing'
import type { SubTemplateValueSetupField } from '../../../types/templateModel'
import { ValueSetupDiffIndicators } from '../../diffIndicators/ValueSetupDiffIndicators'
import { InheritanceIndicator } from '../../inheritanceIndicator/InheritanceIndicator'
import { TemplateDocumentationTooltip } from '../../templateDocumentationTooltip/TemplateDocumentationTooltip'
import { TemplateFieldDocumentation } from '../../templateFieldDocumentation/TemplateFieldDocumentation'
import { stringifySpecialCasedSubTemplateFieldValue } from '../helpers'
import type {
  SpecialCaseComponentProps,
  SpecialCasedSubTemplatedValueSetup,
} from '../types'
import { useSpecialCasedSubTemplatedFieldVariant } from '../useSpecialCasedSubTemplatedFieldVariant'
import { CcCalcStepFieldPriority } from './CcCalcStepFieldPriority'

export const CcCalcStepFieldName = ({ data }: SpecialCaseComponentProps) => {
  const variant = useSpecialCasedSubTemplatedFieldVariant(data)
  const comparableField = useComparableField<SubTemplateValueSetupField>(
    data.field.path,
  )
  const comparableSpecialCasedSubTemplatedValueSetups = useMemo(
    () =>
      ({
        inherited: comparableField.inherited
          ? {
              field: comparableField.inherited,
              subTemplateIdentifier: data.subTemplateIdentifier,
            }
          : null,
        current: comparableField.current
          ? {
              field: comparableField.current,
              subTemplateIdentifier: data.subTemplateIdentifier,
            }
          : null,
        previous: comparableField.previous
          ? {
              field: comparableField.previous,
              subTemplateIdentifier: data.subTemplateIdentifier,
            }
          : null,
      }) satisfies ComparableValue<SpecialCasedSubTemplatedValueSetup>,
    [comparableField, data.subTemplateIdentifier],
  )

  return (
    <div className="relative flex size-full flex-row">
      <TemplateDocumentationTooltip
        tip={
          <Card>
            <CardBody
              outerClassName="min-w-250 max-w-(--breakpoint-2xl)"
              innerClassName="grid grid-cols-[auto_1fr] items-start gap-x-10 gap-y-6"
            >
              <TemplateFieldDocumentation
                field={data.field}
                isExtendedView
                areFieldsShownAsEditable={false}
              />
            </CardBody>
          </Card>
        }
        className="h-full"
      >
        <div className="flex! flex-col justify-center">
          <div className="relative flex items-center justify-between">
            <span className="pl-4">{data.subTemplateIdentifier}</span>
            {!!variant && <InheritanceIndicator value={variant} />}
          </div>
          <CcCalcStepFieldPriority variant={variant} />
        </div>
      </TemplateDocumentationTooltip>
      <div className="mx-2 w-10">
        <ValueSetupDiffIndicators
          comparableValues={comparableSpecialCasedSubTemplatedValueSetups}
          serialiseValueFn={stringifySpecialCasedSubTemplateFieldValue}
        />
      </div>
    </div>
  )
}
